// Import React hooks for state and side-effects
import { useState, useEffect } from 'react';
// Import hooks for accessing route parameters and navigation
import { useParams, useNavigate } from 'react-router-dom';
// Import authentication hook to access current user info
import { useAuth } from '../hooks/useAuth';
// Import axios for HTTP requests
import axios from 'axios';
// Import Course type definition
import type { Course } from '../types';

function ConfirmDeleteCoursePage() {
  // Extract current authenticated user and loading state
  const { user, isLoading } = useAuth();
  // Extract course_id parameter from URL
  const { course_id } = useParams<{ course_id: string }>();
  // Local state to hold fetched course data
  const [course, setCourse] = useState<Course | null>(null);
  // Local state to hold any error messages
  const [error, setError] = useState<string | null>(null);
  // Hook to programmatically navigate between routes
  const navigate = useNavigate();
  // Side effect: runs when component mounts or dependencies change
  useEffect(() => {
    // If not loading and user is not logged in or not a teacher → redirect to courses page
    if (!isLoading && (!user || user.role !== 'teacher')) {
      navigate('/courses');
    } 
    // Otherwise, if we have a course_id from the route params → fetch course details
    else if (course_id) {
      const fetchCourse = async () => {
        try {
          // First request CSRF token before making API calls
          await axios.get('/elearning/get-csrf/', { withCredentials: true });
          // Fetch course details from backend
          const response = await axios.get(`/elearning/api/courses/${course_id}/`, { withCredentials: true });
          // Ensure the logged-in user is the teacher of this course
          if (response.data.teacher.id !== user?.id) {
            throw new Error('You are not authorized to delete this course');
          }
          // Save course data in state
          setCourse(response.data);
          setError(null);
        } catch (err: any) {
          // Handle errors (API error message or generic message)
          const errorMessage = err.response?.data?.error
            ? JSON.stringify(err.response.data.error, null, 2)
            : err.message || 'Failed to fetch course';
          console.error('ConfirmDeleteCoursePage: Error fetching course=', errorMessage);
          setError(errorMessage);
        }
      };
      // Call fetch function
      fetchCourse();
    }
  }, [isLoading, user, course_id, navigate]); // Dependencies for re-running effect
  // Handle course deletion
  const handleDelete = async () => {
    if (!course_id) return; // If no course_id is present, do nothing
    try {
      // Get CSRF token for delete request
      const csrfResponse = await axios.get('/elearning/get-csrf/', { withCredentials: true });
      const csrfToken = csrfResponse.data.csrfToken;
      // Perform delete request with CSRF token
      await axios.delete(`/elearning/api/courses/${course_id}/`, {
        withCredentials: true,
        headers: { 'X-CSRFToken': csrfToken },
      });
      // Redirect back to course list after deletion
      navigate('/courses');
    } catch (err: any) {
      // Handle deletion errors
      const errorMessage = err.response?.data?.detail
        ? JSON.stringify(err.response.data.detail, null, 2)
        : err.message || 'Failed to delete course';
      console.error('ConfirmDeleteCoursePage: Error deleting course=', errorMessage);
      setError(errorMessage);
    }
  };
  // Show loading spinner if still fetching authentication state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-600"></div>
      </div>
    );
  }
  // If user or course is not available → render nothing
  if (!user || !course) return null;
  // Render confirmation UI
  return (
    <div className="min-h-screen bg-gray-100 font-sans">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">Delete Course</h1>

        {/* Show error message if one exists */}
        {error && (
          <div className="mb-8 p-4 bg-red-50 text-red-700 rounded-lg shadow-sm">
            <pre>{error}</pre>
          </div>
        )}
        {/* Confirmation box */}
        <div className="bg-white rounded-lg shadow-md p-6 max-w-2xl">
          <p className="text-gray-700 mb-4">
            Are you sure you want to delete the course <strong>{course.name}</strong>? This action cannot be undone.
          </p>
          <div className="flex space-x-4">
            {/* Confirm delete button */}
            <button
              onClick={handleDelete}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Confirm Delete
            </button>
            {/* Cancel button (redirects back to course) */}
            <button onClick={() => navigate(-1)} className="px-5 py-2 bg-blue-600 text-white font-medium rounded-lg shadow hover:bg-blue-700 transition">
          Cancel
        </button>
          </div>
        </div>
      </div>
    </div>
  );
}
// Export component as default
export default ConfirmDeleteCoursePage;